import BoxInfo from '@/components/boxInfo/boxInfo';
import TextData from '@/components/textData/textData';
import { TextItemDataType } from '@/components/textData/types/textData.type';
import { TRANSLATED_CONTRACT_STATUS } from '@/modules/contract/entities/contants/contract';
import { Contract } from '@/modules/contract/entities/interfaces/contract';
import { LogoProvider } from '@/modules/provider';
import React, { useMemo } from 'react';
import ICONS from '@/shared/entities/constants/icons';
import { formatToUnit } from '@/utils/format';

export enum InfoKey {
  CODE = 'code',
  PROVIDER = 'provider',
  COPAY = 'copay',
  CONTRIBUTION = 'contribution',
  STATUS = 'status',
  VALID_FROM = 'validFrom',
  VALID_UNTIL = 'validUntil',
  MODAL_NAME = 'modalName',
  ENROLLMENT_CODE = 'enrollmentCode',
  REIMBURSEMENT_COEFFICIENT = 'reimbursementCoefficient',
}

interface DetailsProps {
  loading?: boolean;
  contract: Contract;
  items: {
    key: InfoKey;
    size: number;
  }[];
}

const { REACT_APP_ENROLLMENT_CODE_DEFAULT } = process.env;

const Infos: React.FC<DetailsProps> = ({ loading, contract, items }) => {
  const infosToChoose = useMemo<Record<InfoKey, TextItemDataType>>(
    () => ({
      [InfoKey.CODE]: {
        label: 'Código do contrato',
        text: contract.code,
      },
      [InfoKey.PROVIDER]: {
        label: 'Fornecedor',
        text: contract.provider?.name,
        component: contract.provider?.cnpj ? (
          <LogoProvider size='small' providerCnpj={String(contract.provider?.cnpj)} />
        ) : null,
      },
      [InfoKey.COPAY]: {
        label: 'Coparticipação',
        text:
          contract.copayValue && contract.copayUnit
            ? formatToUnit(contract.copayValue, contract.copayUnit)
            : '-',
      },
      [InfoKey.CONTRIBUTION]: {
        label: 'Contribuição',
        text:
          contract.contributionValue && contract.contributionUnit
            ? formatToUnit(contract.contributionValue, contract.contributionUnit)
            : '-',
      },
      [InfoKey.STATUS]: {
        label: 'Status',
        text: TRANSLATED_CONTRACT_STATUS[contract.status],
      },
      [InfoKey.VALID_FROM]: {
        label: 'Início de vigência',
        text: contract.validFrom,
        type: 'date',
      },
      [InfoKey.VALID_UNTIL]: {
        label: 'Fim de vigência',
        text: contract.validUntil,
        type: 'date',
      },
      [InfoKey.MODAL_NAME]: {
        label: 'Modelo de contrato',
        text: contract.model?.name,
      },
      [InfoKey.REIMBURSEMENT_COEFFICIENT]: {
        label: 'Coeficiente de reembolso',
        text: contract.reimbursementCoefficient ? String(contract.reimbursementCoefficient) : '-',
      },
      [InfoKey.ENROLLMENT_CODE]: {
        label: 'Código do angariador',
        text: contract.enrollmentCode || REACT_APP_ENROLLMENT_CODE_DEFAULT,
      },
    }),
    [contract],
  );

  const data = useMemo(() => {
    return items.map(({ key, size }) => ({
      ...infosToChoose[key],
      sizeDefault: size,
    }));
  }, [infosToChoose, items]);

  return (
    <BoxInfo title='Dados do contrato ' icon={ICONS.CONTRACT_DATA}>
      <TextData loading={loading} data={data} />
    </BoxInfo>
  );
};

export default Infos;
